
import { supabase } from '@/integrations/supabase/client';
import { Courier } from '@/services/types/courierTypes';

/**
 * Создание или обновление курьера
 * @param courierData - Данные курьера для сохранения
 * @returns Результат операции
 */
export const upsertCourier = async (courierData: Partial<Courier>) => {
  try {
    if (!courierData.id) {
      console.error('upsertCourier: ID курьера обязателен');
      return { data: null, error: new Error('ID курьера обязателен') };
    }

    // Убедимся, что status имеет правильный тип
    if (courierData.status && !['active', 'inactive'].includes(courierData.status)) {
      courierData.status = 'active' as const; // Используем безопасное значение по умолчанию
    }

    // Убедимся, что name не равен undefined если отсутствует
    if (courierData.name === undefined) {
      courierData.name = 'Новый курьер';
    }
    
    console.log('Обновление/создание курьера:', courierData);
    
    // Создаем правильный объект для upsert, убедившись что поле name присутствует
    const courierForUpsert = {
      ...courierData,
      name: courierData.name || 'Новый курьер' // гарантируем, что name не будет undefined
    };
    
    const { data, error } = await supabase
      .from('couriers')
      .upsert([courierForUpsert], { // Оборачиваем в массив для соответствия типам
        onConflict: 'id',
        ignoreDuplicates: false
      })
      .select();
    
    if (error) {
      console.error('Ошибка при обновлении/создании курьера:', error);
    } else {
      console.log('Курьер успешно обновлен/создан:', data);
    }
    
    return { data, error };
  } catch (error: any) {
    console.error('Ошибка при обновлении/создании курьера:', error);
    return { data: null, error };
  }
};

/**
 * Обновление статуса курьера
 * @param courierId - ID курьера
 * @param status - Новый статус
 * @returns Результат операции
 */
export const updateCourierStatus = async (courierId: string, status: 'active' | 'inactive') => {
  try {
    const { data, error } = await supabase
      .from('couriers')
      .update({ status })
      .eq('id', courierId)
      .select();
    
    return { data, error };
  } catch (error) {
    console.error('Ошибка при обновлении статуса курьера:', error);
    return { data: null, error };
  }
};

/**
 * Обновление email курьера
 * @param courierId - ID курьера
 * @param email - Новый email
 * @returns Результат операции
 */
export const updateCourierEmail = async (courierId: string, email: string) => {
  try {
    const { data, error } = await supabase
      .from('couriers')
      .update({ email })
      .eq('id', courierId)
      .select();
    
    return { ok: !error, data, error };
  } catch (error) {
    console.error('Ошибка при обновлении email курьера:', error);
    return { ok: false, data: null, error };
  }
};

/**
 * Функция обновления тарифа курьера
 * @param courierId - ID курьера
 * @param tariff - Данные тарифа
 * @returns Результат операции
 */
export const updateCourierTariff = async (courierId: string, tariff: { 
  title: string;
  key: string;
  price: number;
}) => {
  try {
    const { data, error } = await supabase
      .from('couriers')
      .update({ 
        tariff: tariff.title,
        tariff_key: tariff.key,
        tariff_price: tariff.price,
        updated_at: new Date().toISOString()
      })
      .eq('id', courierId)
      .select();
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Ошибка при обновлении тарифа курьера:', error);
    return { data: null, error };
  }
};

/**
 * Функция для удаления курьера и всех связанных данных
 * Порядок удаления исправлен для избежания ошибок внешних ключей
 * @param courierId - ID курьера
 * @returns Результат операции с детальной информацией об удаленных записях
 */
export const deleteCourierWithRelatedData = async (courierId: string) => {
  try {
    console.log(`Начинаем каскадное удаление курьера ${courierId}`);
    
    // 1. ПЕРВЫМ удаляем историю отслеживания велосипедов (может ссылаться на rental_id)
    console.log(`Удаление истории отслеживания для курьера ${courierId}`);
    const { data: trackingData, error: trackingError } = await supabase
      .from('bike_tracking_history')
      .delete()
      .eq('courier_id', courierId)
      .select();

    if (trackingError) {
      console.error('Ошибка при удалении истории отслеживания:', trackingError);
      return { success: false, error: trackingError };
    }
    console.log(`Удалено ${trackingData?.length || 0} записей истории отслеживания`);

    // 2. Удаляем платежи курьера (может ссылаться на subscription_id и rental_id)
    console.log(`Удаление платежей для курьера ${courierId}`);
    const { data: paymentsData, error: paymentsError } = await supabase
      .from('payments')
      .delete()
      .eq('courier_id', courierId)
      .select();

    if (paymentsError) {
      console.error('Ошибка при удалении платежей:', paymentsError);
      return { success: false, error: paymentsError };
    }
    console.log(`Удалено ${paymentsData?.length || 0} платежей`);

    // 3. Находим и удаляем подписки через связанные аренды
    console.log(`Поиск и удаление подписок для курьера ${courierId}`);
    
    // Сначала получаем все rental_id для данного курьера
    const { data: rentalsForSubscriptions, error: rentalsQueryError } = await supabase
      .from('rentals')
      .select('id')
      .eq('courier_id', courierId);

    if (rentalsQueryError) {
      console.error('Ошибка при получении аренд для удаления подписок:', rentalsQueryError);
      return { success: false, error: rentalsQueryError };
    }

    const rentalIds = rentalsForSubscriptions?.map(r => r.id) || [];
    
    // Теперь удаляем подписки, связанные с этими арендами
    let subscriptionsData = [];
    if (rentalIds.length > 0) {
      const { data: subsData, error: subscriptionsError } = await supabase
        .from('subscriptions')
        .delete()
        .in('rental_id', rentalIds)
        .select();

      if (subscriptionsError) {
        console.error('Ошибка при удалении подписок:', subscriptionsError);
        return { success: false, error: subscriptionsError };
      }
      subscriptionsData = subsData || [];
    }
    
    console.log(`Удалено ${subscriptionsData.length} подписок`);

    // 4. Удаляем аренды курьера
    console.log(`Удаление аренд для курьера ${courierId}`);
    const { data: rentalsData, error: rentalsError } = await supabase
      .from('rentals')
      .delete()
      .eq('courier_id', courierId)
      .select();

    if (rentalsError) {
      console.error('Ошибка при удалении аренд:', rentalsError);
      return { success: false, error: rentalsError };
    }
    console.log(`Удалено ${rentalsData?.length || 0} аренд`);

    // 5. Наконец, удаляем самого курьера
    console.log(`Удаление курьера ${courierId}`);
    const { data: courierData, error: courierError } = await supabase
      .from('couriers')
      .delete()
      .eq('id', courierId)
      .select();

    if (courierError) {
      console.error('Ошибка при удалении курьера:', courierError);
      return { success: false, error: courierError };
    }
    console.log(`Курьер удален: ${JSON.stringify(courierData)}`);

    const deletionSummary = {
      courierId,
      trackingRecords: trackingData?.length || 0,
      payments: paymentsData?.length || 0,
      subscriptions: subscriptionsData.length || 0,
      rentals: rentalsData?.length || 0,
      courier: courierData?.length || 0
    };

    console.log('Каскадное удаление завершено:', deletionSummary);
    return { success: true, data: deletionSummary };
  } catch (error: any) {
    console.error('Непредвиденная ошибка при удалении курьера:', error);
    return { success: false, error };
  }
};
